import { useCallback, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobErrorBlock } from './JobPollingPanel';

const MAX_DISPLAY_ROWS = 50;
const APPLY_OPERATION = 'channel_commission_backfill_apply';

export function SmoobuCommissionBackfillPanel() {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyStarting, setApplyStarting] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [results, setResults] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // Apply used to trigger a full paginated Smoobu API resync (365-day
  // window, per-booking DB writes) synchronously in-request. It now starts
  // a JobStore background job and this panel polls it via the shared hook.
  const applyPoll = useJobPolling({
    fetchStatus: diagnosticsAPI.smoobuBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setResults(result);
      setApplyError(null);
      if (result.commission_populated_count > 0) {
        toast.success(
          `Commission backfill applied: ${result.commission_populated_count} bookings updated, ` +
          `net adjustment: ${result.net_adjustment_total}`
        );
      }
    }, []),
    onFailed: useCallback((error) => { setApplyError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => diagnosticsAPI.smoobuBackfillJobLatest(APPLY_OPERATION), poll: applyPoll });

  const runPreview = async () => {
    setPreviewLoading(true);
    setResults(null);
    setApplyError(null);
    setConfirmApply(false);
    try {
      const { data } = await diagnosticsAPI.smoobuBackfillChannelCommissionPreview();
      setResults(data);
    } catch (err) {
      setResults({ error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    setConfirmApply(false);
    setApplyStarting(true);
    setResults(null);
    setApplyError(null);
    try {
      const { data } = await diagnosticsAPI.smoobuBackfillChannelCommissionApplyStart();
      if (data.job_id) applyPoll.start(data.job_id);
    } catch (err) {
      setApplyError(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const running = previewLoading || applyStarting || applyPoll.polling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Smoobu Channel Commission Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Populates channel_commission and re-derives net_revenue = gross - commission
          for Smoobu bookings. Preview shows affected bookings; Apply re-syncs from
          the Smoobu API with the corrected parser.
        </p>

        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={running} data-testid="smoobu-commission-backfill-preview">
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={running}
              data-testid="smoobu-commission-backfill-apply"
            >
              Apply
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={runApply}
              disabled={running}
              data-testid="smoobu-commission-backfill-confirm-apply"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Confirm Apply
            </Button>
          )}
        </div>

        <JobErrorBlock error={applyError} testId="smoobu-commission-backfill-apply-error" />

        {results && (
          <div className="space-y-4" data-testid="smoobu-commission-backfill-result">
            {results.error && (
              <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                Error: {results.error}
              </div>
            )}

            {/* Dry-run results */}
            {results.dry_run && !results.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Missing commission</p>
                    <p className="text-xl font-bold tabular-nums">{results.affected_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Already populated</p>
                    <p className="text-xl font-bold tabular-nums">{results.already_populated_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Total active</p>
                    <p className="text-xl font-bold tabular-nums">{results.total_active_bookings}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Mode</p>
                    <Badge variant="secondary">Preview</Badge>
                  </div>
                </div>

                {results.by_channel && Object.keys(results.by_channel).length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">By channel: </span>
                    {Object.entries(results.by_channel).map(([ch, n]) => (
                      <Badge key={ch} variant="outline" className="mr-1">{ch}: {n}</Badge>
                    ))}
                  </div>
                )}

                <p className="text-sm text-muted-foreground">{results.message}</p>

                {/* Booking table */}
                {results.bookings?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {results.bookings.length > MAX_DISPLAY_ROWS
                        ? `Showing ${MAX_DISPLAY_ROWS} of ${results.bookings.length} bookings`
                        : `${results.bookings.length} booking${results.bookings.length !== 1 ? 's' : ''}`}
                    </p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-3 py-2 font-medium">ID</th>
                            <th className="text-left px-3 py-2 font-medium">Property</th>
                            <th className="text-left px-3 py-2 font-medium">Channel</th>
                            <th className="text-left px-3 py-2 font-medium">Check-in</th>
                            <th className="text-right px-3 py-2 font-medium">Gross</th>
                            <th className="text-right px-3 py-2 font-medium">Current Net</th>
                            <th className="text-right px-3 py-2 font-medium">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.bookings.slice(0, MAX_DISPLAY_ROWS).map((r, i) => (
                            <tr key={r.smoobu_id || i} className="border-b last:border-0">
                              <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                              <td className="px-3 py-2 text-xs">{r.property_name}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px]">{r.channel}</Badge>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {r.total_price != null ? Number(r.total_price).toFixed(2) : '\u2014'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-400">
                                {r.old_net_revenue != null ? Number(r.old_net_revenue).toFixed(2) : '\u2014'}
                                <span className="text-xs text-muted-foreground ml-1">(= gross)</span>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                pending sync
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Apply results */}
            {results.dry_run === false && !results.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Commission populated</p>
                    <p className="text-xl font-bold tabular-nums">{results.commission_populated_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Total commission</p>
                    <p className="text-xl font-bold tabular-nums">{results.total_commission_sum}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Net adjustment</p>
                    <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                      {results.net_adjustment_total}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Flagged</p>
                    <p className="text-xl font-bold tabular-nums">
                      {results.flagged_count || 0}
                    </p>
                  </div>
                </div>

                {results.sync_result && (
                  <div className="text-sm text-muted-foreground">
                    Sync: {results.sync_result.synced} fetched,{' '}
                    {results.sync_result.created} created,{' '}
                    {results.sync_result.updated} updated
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Commission backfill complete. Check the Bookings list to verify.
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
